import { MoldOrder } from '../../../types/mold';
import { StepConfig, StepCondition, StepVisibilityCondition, ConditionOperator } from '../../../types/steps';

// Enable debug logging for step visibility (set to false in production)
const DEBUG_STEP_VISIBILITY = process.env.NODE_ENV && [
    'development',
    'test',
].includes(process.env.NODE_ENV);

/**
 * Gets a nested property value from an object using dot notation
 * @param obj - The object to get the value from
 * @param path - The dot notation path (e.g., 'mold.remarks')
 * @returns The value at the specified path or undefined if not found
 */
function getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
        return current && typeof current === 'object' ? current[key] : undefined;
    }, obj);
}

/**
 * Evaluates a single condition against a mold order
 * @param moldOrder - The mold order to evaluate
 * @param condition - The condition to check
 * @returns True if the condition is met, false otherwise
 */
function evaluateCondition(moldOrder: MoldOrder, condition: StepCondition): boolean {
    // Handle missing field gracefully
    if (!condition.field) {
        console.warn('Step condition missing field property:', condition);
        return false;
    }

    const value = getNestedValue(moldOrder, condition.field);

    // Debug logging for condition evaluation
    if (DEBUG_STEP_VISIBILITY) {
        console.debug(`Evaluating condition: ${condition.field} ${condition.operator} ${condition.value}, actual value:`, value);
    }

    switch (condition.operator) {
        case ConditionOperator.EXISTS:
            return value !== undefined && value !== null && value !== '';

        case ConditionOperator.NOT_EXISTS:
            return value === undefined || value === null || value === '';

        case ConditionOperator.EQUALS:
            return value === condition.value;

        case ConditionOperator.NOT_EQUALS:
            return value !== condition.value;

        case ConditionOperator.CONTAINS:
            if (typeof value === 'string') {
                return value.includes(condition.value);
            }
            if (Array.isArray(value)) {
                return value.includes(condition.value);
            }
            return false;

        case ConditionOperator.NOT_CONTAINS:
            if (typeof value === 'string') {
                return !value.includes(condition.value);
            }
            if (Array.isArray(value)) {
                return !value.includes(condition.value);
            }
            return true;

        case ConditionOperator.GREATER_THAN:
            return typeof value === 'number' && value > condition.value;

        case ConditionOperator.LESS_THAN:
            return typeof value === 'number' && value < condition.value;

        default:
            console.warn('Unknown condition operator:', condition.operator);
            return false;
    }
}

/**
 * Evaluates visibility conditions for a step (supports nested condition groups)
 * @param moldOrder - The mold order to evaluate
 * @param visibilityCondition - The visibility condition configuration
 * @returns True if the step should be visible, false otherwise
 */
function evaluateVisibilityCondition(
    moldOrder: MoldOrder,
    visibilityCondition: StepVisibilityCondition
): boolean {
    const { conditions = [], conditionGroups = [], logicalOperator = 'AND' } = visibilityCondition;

    // Collect all results from direct conditions
    const directConditionResults = conditions.map(condition => evaluateCondition(moldOrder, condition));

    // Collect all results from nested condition groups
    const nestedGroupResults = conditionGroups.map(group => evaluateVisibilityCondition(moldOrder, group));

    // Combine all results
    const allResults = [...directConditionResults, ...nestedGroupResults];

    // If no conditions or groups, default to visible
    if (allResults.length === 0) {
        return true;
    }

    // Apply logical operator
    const result = logicalOperator === 'OR'
        ? allResults.some(result => result)
        : allResults.every(result => result);

    // Debug logging for visibility evaluation
    if (DEBUG_STEP_VISIBILITY) {
        console.debug(`Visibility evaluation - operator: ${logicalOperator}, results:`, allResults, 'final result:', result);
    }

    return result;
}

/**
 * Filters step configurations based on visibility conditions
 * @param stepConfigs - Array of step configurations
 * @param moldOrder - The mold order to evaluate conditions against
 * @returns Filtered array of step configurations that should be visible
 */
export function filterVisibleSteps(
    stepConfigs: StepConfig[],
    moldOrder: MoldOrder
): StepConfig[] {
    if (DEBUG_STEP_VISIBILITY) {
        console.debug('Filtering steps for mold order:', moldOrder.id, 'status:', moldOrder.status);
    }

    const visibleSteps = stepConfigs.filter(stepConfig => {
        // If no visibility condition is defined, the step is always visible
        if (!stepConfig.visibilityCondition) {
            return true;
        }

        const isVisible = evaluateVisibilityCondition(moldOrder, stepConfig.visibilityCondition);
        if (DEBUG_STEP_VISIBILITY) {
            console.debug(`Step ${stepConfig.id} (${stepConfig.titleSuffix}) - visibility result:`, isVisible);
        }
        return isVisible;
    });

    if (DEBUG_STEP_VISIBILITY) {
        console.debug(`Filtered ${visibleSteps.length} visible steps out of ${stepConfigs.length} total steps`);
    }
    return visibleSteps;
}

/**
 * Checks if a specific step should be visible
 * @param stepConfig - The step configuration to check
 * @param moldOrder - The mold order to evaluate conditions against
 * @returns True if the step should be visible, false otherwise
 */
export function isStepVisible(stepConfig: StepConfig, moldOrder: MoldOrder): boolean {
    if (!stepConfig.visibilityCondition) {
        return true;
    }

    return evaluateVisibilityCondition(moldOrder, stepConfig.visibilityCondition);
}
